import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import { gql } from '../../utils/graphql';
import instaIcon from '../../assets/icons/instagram.svg';
import tiktokIcon from '../../assets/icons/tiktok.svg';

interface ProfileProps {
	ambassador: any;
	token: string;
	refreshData: () => void;
}

interface PublicEvent {
	id: string;
	name: string;
	city?: string;
	date?: string;
}

const Profile: React.FC<ProfileProps> = ({ ambassador, token, refreshData }) => {
	const [passData, setPassData] = useState({ current: '', new: '', confirm: '' });
	const [passMsg, setPassMsg] = useState('');
	const [loadingPass, setLoadingPass] = useState(false);

	const [allEvents, setAllEvents] = useState<PublicEvent[]>([]);
	const [selectedEventId, setSelectedEventId] = useState('');
	const [eventMsg, setEventMsg] = useState('');
	const [loadingEvent, setLoadingEvent] = useState(false);

	useEffect(() => {
		const fetchPublicEvents = async () => {
			const query = `
				query GetPublicEvents {
					publicEvents {
						id
						name
						city
						date
					}
				}
			`;
			try {
				const response = await gql(query, {}, token);
				if (response.data?.data?.publicEvents) {
					setAllEvents(response.data.data.publicEvents);
				}
			} catch (err) {
				console.error('Failed to fetch public events', err);
			}
		};
		fetchPublicEvents();
	}, [token]);

	const currentEventIds = new Set((ambassador.events || []).map((e: any) => e.id));
	const availableEvents = allEvents.filter(e => !currentEventIds.has(e.id));

	const handleAddEvent = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedEventId) return;

		setLoadingEvent(true);
		setEventMsg('');

		try {
			const currentIds = (ambassador.events || []).map((e: any) => e.id);
			const newEventIds = [...currentIds, selectedEventId];

			const query = `
				mutation UpdateProfile($id: ID!, $input: UpdateAmbassadorInput!) {
					updateAmbassador(id: $id, input: $input) {
						id
						events {
							id
							name
							city
							date
							inviteId
						}
					}
				}
			`;

			const response = await gql(query, {
				id: ambassador.id,
				input: {
					eventIds: newEventIds
				}
			}, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setEventMsg('Evenimentul a fost adăugat cu succes!');
			setSelectedEventId('');
			refreshData();
			setTimeout(() => setEventMsg(''), 3000);
		} catch (err: any) {
			setEventMsg(`Error: ${err.message}`);
		} finally {
			setLoadingEvent(false);
		}
	};



	const [socials, setSocials] = useState({
		instagram: ambassador.instagram || '',
		tiktok: ambassador.tiktok || '',
	});
	const [socialMsg, setSocialMsg] = useState('');
	const [loadingSocials, setLoadingSocials] = useState(false);

	const [profileData, setProfileData] = useState({
		firstName: ambassador.firstName || '',
		lastName: ambassador.lastName || '',
		email: ambassador.email || '',
		telephone: ambassador.telephone || '',
	});
	const [profileMsg, setProfileMsg] = useState('');
	const [loadingProfile, setLoadingProfile] = useState(false);

	useEffect(() => {
		if (ambassador) {
			setProfileData({
				firstName: ambassador.firstName || '',
				lastName: ambassador.lastName || '',
				email: ambassador.email || '',
				telephone: ambassador.telephone || '',
			});
			setSocials({
				instagram: ambassador.instagram || '',
				tiktok: ambassador.tiktok || '',
			});
		}
	}, [ambassador]);



	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (passData.new !== passData.confirm) {
			setPassMsg('Noile parole nu se potrivesc');
			return;
		}

		setLoadingPass(true);
		setPassMsg('');

		try {
			const query = `
				mutation ChangePass($input: UpdatePasswordInput!) {
					changeAmbassadorPassword(input: $input)
				}
			`;

			const response = await gql(query, {
				input: {
					currentPassword: passData.current,
					newPassword: passData.new
				}
			}, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setPassMsg('Parola a fost schimbată cu succes!');
			setPassData({ current: '', new: '', confirm: '' });
		} catch (err: any) {
			setPassMsg(`Error: ${err.message}`);
		} finally {
			setLoadingPass(false);
		}
	};

	const handleSaveSocials = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoadingSocials(true);
		setSocialMsg('');

		try {
			const query = `
				mutation UpdateProfile($id: ID!, $input: UpdateAmbassadorInput!) {
					updateAmbassador(id: $id, input: $input) {
						id
						instagram
						tiktok
					}
				}
			`;

			const response = await gql(query, {
				id: ambassador.id,
				input: {
					instagram: socials.instagram,
					tiktok: socials.tiktok,
				}
			}, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setSocialMsg('Rețelele de socializare au fost actualizate!');
			refreshData();
			setTimeout(() => setSocialMsg(''), 3000);
		} catch (err: any) {
			setSocialMsg(`Error: ${err.message}`);
		} finally {
			setLoadingSocials(false);
		}
	};

	const handleSaveProfile = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoadingProfile(true);
		setProfileMsg('');

		try {
			const query = `
				mutation UpdateProfile($id: ID!, $input: UpdateAmbassadorInput!) {
					updateAmbassador(id: $id, input: $input) {
						id
						firstName
						lastName
						email
						telephone
					}
				}
			`;

			const response = await gql(query, {
				id: ambassador.id,
				input: profileData
			}, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setProfileMsg('Profilul a fost actualizat!');
			refreshData();
			setTimeout(() => setProfileMsg(''), 3000);
		} catch (err: any) {
			setProfileMsg(`Error: ${err.message}`);
		} finally {
			setLoadingProfile(false);
		}
	};

	const handleDeleteAccount = async () => {
		if (!window.confirm('Esti sigur ca vrei sa stergi contul? Aceasta actiune este ireversibila!')) return;

		try {
			const mutation = `
				mutation DeleteAmbassador($id: ID!) {
					deleteAmbassador(id: $id)
				}
			`;
			const response = await gql(mutation, { id: ambassador.id }, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			window.location.reload();
		} catch (err: any) {
			alert(`Eroare la stergerea contului: ${err.message}`);
		}
	};

	return (
		<div>
			{/* Profile Picture */}


			{/* Account Details */}
			<div className={styles.card}>
				<div className={styles.cardTitle}>Detaliile Contului</div>
				<form onSubmit={handleSaveProfile}>
					<div className={styles.detailGrid}>
						<div className={styles.detailItem}>
							<label>Prenume</label>
							<input
								type="text"
								className={styles.inputBase}
								value={profileData.firstName}
								onChange={e => setProfileData({ ...profileData, firstName: e.target.value })}
								required
							/>
						</div>
						<div className={styles.detailItem}>
							<label>Nume</label>
							<input
								type="text"
								className={styles.inputBase}
								value={profileData.lastName}
								onChange={e => setProfileData({ ...profileData, lastName: e.target.value })}
								required
							/>
						</div>
						<div className={styles.detailItem}>
							<label>Email</label>
							<input
								type="email"
								className={styles.inputBase}
								value={profileData.email}
								onChange={e => setProfileData({ ...profileData, email: e.target.value })}
								required
							/>
						</div>
						<div className={styles.detailItem}>
							<label>Telefon</label>
							<input
								type="text"
								className={styles.inputBase}
								value={profileData.telephone}
								onChange={e => setProfileData({ ...profileData, telephone: e.target.value })}
								required
							/>
						</div>
					</div>

					{profileMsg && (
						<div className={`${styles.msgBox} ${profileMsg.includes('Error') ? styles.msgError : styles.msgSuccess}`}>
							{profileMsg}
						</div>
					)}

					<button type="submit" className={styles.button} disabled={loadingProfile} style={{ marginTop: '20px' }}>
						{loadingProfile ? 'Se salvează...' : 'Salvează Detaliile'}
					</button>
				</form>
			</div>

			{/* Events Management */}
			<div className={styles.card}>
				<div className={styles.cardTitle}>Evenimentele Mele</div>
				<p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '-0.5rem', marginBottom: '1.25rem' }}>
					Selectează evenimentele pe care dorești să le promovezi pentru a putea revendica invitații și premii specifice fiecărui oraș.
				</p>
				<div className={styles.eventsSection}>
					{/* Current enrolled events */}
					<div>
						<label className={styles.inputLabel}>Evenimente Asignate</label>
						{ambassador.events && ambassador.events.length > 0 ? (
							<div className={styles.eventChipsList}>
								{ambassador.events.map((evt: any) => (
									<div key={evt.id} className={styles.eventChip}>
										<span className={styles.eventChipName}>{evt.name}</span>
										{evt.city && <span className={styles.eventChipCity}>{evt.city}</span>}
										{evt.date && (
											<span className={styles.eventChipDate}>
												{new Date(evt.date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' })}
											</span>
										)}
									</div>
								))}
							</div>
						) : (
							<div className={styles.emptyEventsHint}>
								Nu ești înscris la niciun eveniment momentan.
							</div>
						)}
					</div>

					{/* Add new event */}
					<div className={styles.addEventBlock}>
						<label className={styles.inputLabel}>Adaugă Eveniment Nou</label>
						{availableEvents.length > 0 ? (
							<form onSubmit={handleAddEvent} className={styles.addEventForm}>
								<select
									className={styles.inputBase}
									value={selectedEventId}
									onChange={e => setSelectedEventId(e.target.value)}
									disabled={loadingEvent}
									style={{ marginBottom: 0 }}
								>
									<option value="" disabled>Selectează un eveniment...</option>
									{availableEvents.map((evt: any) => (
										<option key={evt.id} value={evt.id}>
											{evt.name}{evt.city ? ` (${evt.city})` : ''}
										</option>
									))}
								</select>
								<button
									type="submit"
									className={styles.button}
									disabled={!selectedEventId || loadingEvent}
									style={{ whiteSpace: 'nowrap' }}
								>
									{loadingEvent ? 'Se adaugă...' : 'Adaugă'}
								</button>
							</form>
						) : (
							<div className={styles.allEventsEnrolledNotice}>
								{allEvents.length === 0
									? 'Nu există evenimente disponibile de adăugat.'
									: 'Ești înscris la toate evenimentele disponibile.'}
							</div>
						)}
					</div>

					{eventMsg && (
						<div className={`${styles.msgBox} ${eventMsg.includes('Error') ? styles.msgError : styles.msgSuccess}`} style={{ marginTop: '0.5rem', marginBottom: 0 }}>
							{eventMsg}
						</div>
					)}
				</div>
			</div>

			{/* Social Media */}
			<div className={styles.card}>
				<div className={styles.cardTitle}>Social Media</div>
				<form onSubmit={handleSaveSocials} style={{ maxWidth: '420px' }}>
					<div className={styles.inputGroup}>
						<span className={styles.inputPrefix}>
							<img src={instaIcon} alt="Instagram" width="18" />
						</span>
						<input
							type="text"
							className={styles.inputWithPrefix}
							placeholder="Nume utilizator Instagram"
							value={socials.instagram}
							onChange={e => setSocials({ ...socials, instagram: e.target.value })}
						/>
					</div>
					<div className={styles.inputGroup}>
						<span className={styles.inputPrefix}>
							<img src={tiktokIcon} alt="TikTok" width="18" />
						</span>
						<input
							type="text"
							className={styles.inputWithPrefix}
							placeholder="Nume utilizator TikTok"
							value={socials.tiktok}
							onChange={e => setSocials({ ...socials, tiktok: e.target.value })}
						/>
					</div>

					{socialMsg && (
						<div className={`${styles.msgBox} ${socialMsg.includes('Error') ? styles.msgError : styles.msgSuccess}`}>
							{socialMsg}
						</div>
					)}

					<button type="submit" className={styles.button} disabled={loadingSocials}>
						{loadingSocials ? 'Se salvează...' : 'Salvează Socials'}
					</button>
				</form>
			</div>

			{/* Change Password */}
			<div className={styles.card}>
				<div className={styles.cardTitle}>Schimbă Parola</div>
				<form onSubmit={handleChangePassword} style={{ maxWidth: '420px' }}>
					<input
						type="password"
						className={styles.inputBase}
						placeholder="Parola Curentă"
						value={passData.current}
						onChange={e => setPassData({ ...passData, current: e.target.value })}
						required
					/>
					<input
						type="password"
						className={styles.inputBase}
						placeholder="Parola Nouă"
						value={passData.new}
						onChange={e => setPassData({ ...passData, new: e.target.value })}
						required
					/>
					<input
						type="password"
						className={styles.inputBase}
						placeholder="Confirmă Parola Nouă"
						value={passData.confirm}
						onChange={e => setPassData({ ...passData, confirm: e.target.value })}
						required
					/>

					{passMsg && (
						<div className={`${styles.msgBox} ${passMsg.includes('Error') ? styles.msgError : styles.msgSuccess}`}>
							{passMsg}
						</div>
					)}

					<button type="submit" className={styles.button} disabled={loadingPass}>
						{loadingPass ? 'Se actualizează...' : 'Actualizează Parola'}
					</button>
				</form>
			</div>

			{/* Danger Zone */}
			{/* Delete Account */}
			<div className={styles.dangerZone}>
				<button
					className={styles.button}
					onClick={handleDeleteAccount}
					style={{ backgroundColor: 'red', color: 'white', border: 'none' }}
				>
					Șterge Contul
				</button>
			</div>
		</div>
	);
};

export default Profile;
